import { useEffect, useState, useCallback } from "react";
import { http, downloadAuthenticated } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { confirmDialog, promptDialog } from "@/lib/confirmDialog";
import {
  Database,
  Download,
  RefreshCw,
  Trash2,
  Save,
  HardDriveDownload,
  Upload,
  PlayCircle,
} from "lucide-react";

function fmtBytes(n) {
  if (!n && n !== 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("el-GR");
  } catch {
    return iso;
  }
}

async function confirmTwice(msg) {
  if (!(await confirmDialog(msg))) return false;
  if (!(await confirmDialog(`ΕΠΙΒΕΒΑΙΩΣΗ: ${msg}\n\nΕίστε σίγουρος;`))) return false;
  return true;
}

export default function BackupPanel({ supervisorMode = false }) {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [b, s] = await Promise.all([http.get("/backup/list"), http.get("/settings")]);
      setBackups(b.data.items || []);
      setSettings(s.data);
    } catch {
      toast.error("Σφάλμα φόρτωσης");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(refresh, 0);
    return () => clearTimeout(t);
  }, [refresh]);

  const runNow = async () => {
    setLoading(true);
    try {
      const r = await http.post("/backup/run-now");
      toast.success(`Δημιουργήθηκε backup ${r.data.meta.id}`);
      refresh();
    } catch {
      toast.error("Αποτυχία backup");
    } finally {
      setLoading(false);
    }
  };

  const onDownload = (b) => {
    downloadAuthenticated(
      `/backup/${b.id}/download`,
      {},
      b.filename || `${b.id}.zip`,
      { folder: "backup", saveToCompanion: !supervisorMode },
    )
      .then((r) => toast.success(
        r.viaCompanion
          ? `Αποθηκεύτηκε: ${r.savedTo}`
          : "Λήφθηκε το backup",
      ))
      .catch((e) => toast.error(
        e?.message?.includes("Companion")
          ? "Ο Companion agent δεν αποκρίνεται (πόρτα 9100)"
          : "Αποτυχία λήψης backup",
      ));
  };

  const onDelete = async (id) => {
    if (!(await confirmDialog(`Διαγραφή backup ${id};`))) return;
    try {
      await http.delete(`/backup/${id}`);
      toast.success("Διαγράφηκε");
      refresh();
    } catch {
      toast.error("Αποτυχία διαγραφής");
    }
  };

  const onRestore = async (id) => {
    if (!(await confirmTwice(`Επαναφορά από ${id};\nΘα αντικατασταθούν ΟΛΑ τα δεδομένα.`))) return;
    let code = "supervisor";
    if (!supervisorMode) {
      code = (await promptDialog("Κωδικός διαχειριστή για επιβεβαίωση:"));
      if (!code) return;
    }
    try {
      const r = await http.post(`/backup/restore/${id}`, { admin_code: code });
      toast.success(`Επαναφέρθηκαν: ${Object.values(r.data.restored).reduce((a, b) => a + b, 0)} εγγραφές`);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Αποτυχία επαναφοράς");
    }
  };

  const onManualDownload = (fmt) => {
    const stamp = new Date().toISOString().slice(0, 10);
    const ext = fmt === "json" ? "zip" : "xlsx";
    downloadAuthenticated(
      "/backup/manual",
      { fmt },
      `backup-manual-${stamp}.${ext}`,
      { folder: "backup", saveToCompanion: !supervisorMode },
    )
      .then((r) => toast.success(
        r.viaCompanion
          ? `Αποθηκεύτηκε: ${r.savedTo}`
          : "Λήφθηκε το backup",
      ))
      .catch((e) => toast.error(
        e?.message?.includes("Companion")
          ? "Ο Companion agent δεν αποκρίνεται (πόρτα 9100)"
          : "Αποτυχία λήψης backup",
      ));
  };

  const onSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await http.put("/settings", settings);
      toast.success("Αποθηκεύτηκαν");
    } catch {
      toast.error("Αποτυχία αποθήκευσης");
    } finally {
      setSavingSettings(false);
    }
  };

  const onRestoreUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!(await confirmTwice(`Επαναφορά από αρχείο «${file.name}»;\nΘα αντικατασταθούν ΟΛΑ τα δεδομένα.`))) return;
    let code = "supervisor";
    if (!supervisorMode) {
      code = (await promptDialog("Κωδικός διαχειριστή:"));
      if (!code) return;
    }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("admin_code", code);
    try {
      const r = await http.post("/backup/restore-upload", fd);
      toast.success(`Επαναφέρθηκαν: ${Object.values(r.data.restored).reduce((a, b) => a + b, 0)} εγγραφές`);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Αποτυχία επαναφοράς");
    }
  };

  return (
    <Card className="p-6 space-y-4" data-testid="backup-panel">
      <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
        <Database size={18} /> Backup
      </h3>

      {/* Auto backup settings */}
      {settings && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 p-3 bg-slate-50 border border-slate-200 rounded-md items-end">
          <div className="md:col-span-2 flex items-center gap-3">
            <Switch
              checked={!!settings.backup_auto_enabled}
              onCheckedChange={(v) => setSettings({ ...settings, backup_auto_enabled: v })}
              data-testid="backup-auto-enabled"
            />
            <div>
              <div className="font-bold text-sm">Αυτόματο backup ενεργό</div>
              <div className="text-[11px] text-slate-500">
                Τα αυτόματα backups αποθηκεύονται στον server (ZIP, χωρίς όριο μεγέθους).
              </div>
            </div>
          </div>
          <div>
            <Label className="text-xs">Κάθε X λεπτά</Label>
            <Input
              type="number"
              min={1}
              value={settings.backup_auto_interval_minutes}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  backup_auto_interval_minutes: Number(e.target.value) || 60,
                })
              }
              className="h-9"
              data-testid="backup-auto-interval"
            />
          </div>
          <div>
            <Label className="text-xs">Πόσα να κρατάει</Label>
            <Input
              type="number"
              min={1}
              value={settings.backup_auto_retention}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  backup_auto_retention: Number(e.target.value) || 20,
                })
              }
              className="h-9"
              data-testid="backup-auto-retention"
            />
          </div>
          <div className="md:col-span-4 flex flex-wrap gap-2 pt-1">
            <Button
              onClick={onSaveSettings}
              disabled={savingSettings}
              className="bg-emerald-600 hover:bg-emerald-700 h-9"
              data-testid="backup-save-settings"
            >
              <Save size={14} className="mr-1.5" /> Αποθήκευση ρυθμίσεων
            </Button>
            <Button onClick={runNow} disabled={loading} className="bg-blue-600 hover:bg-blue-700 h-9" data-testid="backup-run-now">
              <PlayCircle size={14} className="mr-1.5" /> Εκτέλεση τώρα
            </Button>
            <Button onClick={refresh} variant="outline" className="h-9" data-testid="backup-refresh">
              <RefreshCw size={14} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} />
              Ανανέωση
            </Button>
          </div>
        </div>
      )}

      {/* Manual backup */}
      <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
        <div className="font-bold text-sm mb-2 flex items-center gap-1.5">
          <HardDriveDownload size={14} /> Χειροκίνητο backup (λήψη στον υπολογιστή σας)
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => onManualDownload("json")} variant="outline" className="h-9" data-testid="manual-backup-json">
            <Download size={14} className="mr-1.5" /> Λήψη ZIP
          </Button>
          <Button onClick={() => onManualDownload("xlsx")} variant="outline" className="h-9" data-testid="manual-backup-xlsx">
            <Download size={14} className="mr-1.5" /> Λήψη XLSX
          </Button>
          <label className="inline-flex items-center gap-1.5 h-9 px-3 border-2 border-slate-300 bg-white rounded-md cursor-pointer hover:bg-slate-50 text-sm font-bold">
            <Upload size={14} />
            Επαναφορά από αρχείο
            <input
              type="file"
              accept=".json,.zip"
              className="hidden"
              onChange={onRestoreUpload}
              data-testid="restore-upload-input"
            />
          </label>
        </div>
      </div>

      {/* Backup list */}
      <div className="border border-slate-200 rounded-md overflow-hidden">
        <div className="grid grid-cols-[1fr_180px_120px_280px] gap-2 px-3 py-2 bg-slate-100 text-[11px] font-bold uppercase tracking-wider text-slate-600">
          <div>ID</div>
          <div>Δημιουργία</div>
          <div>Μέγεθος</div>
          <div></div>
        </div>
        <div className="max-h-[40vh] overflow-y-auto scroll-thin divide-y divide-slate-100" data-testid="backups-list">
          {backups.length === 0 && (
            <div className="p-6 text-center text-slate-400 text-sm">Δεν υπάρχουν αυτόματα backups</div>
          )}
          {backups.map((b) => (
            <div key={b.id} className="grid grid-cols-[1fr_180px_120px_280px] gap-2 px-3 py-2 text-sm items-center">
              <div className="font-mono text-xs truncate" title={b.id}>{b.id}</div>
              <div className="text-xs text-slate-600">{fmtDate(b.created_at)}</div>
              <div className="text-xs font-mono">{fmtBytes(b.size_bytes)}</div>
              <div className="flex gap-1 justify-end">
                <Button size="sm" variant="outline" onClick={() => onDownload(b)} data-testid={`backup-dl-${b.id}`}>
                  <Download size={12} className="mr-1" /> Λήψη
                </Button>
                <Button size="sm" variant="outline" onClick={() => onRestore(b.id)} className="text-amber-700" data-testid={`backup-restore-${b.id}`}>
                  Επαναφορά
                </Button>
                <Button size="sm" variant="outline" onClick={() => onDelete(b.id)} className="text-red-700" data-testid={`backup-del-${b.id}`}>
                  <Trash2 size={12} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}
